import { state, style, trigger } from '@angular/animations';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, DestroyRef, OnInit, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { BulkImportType } from '@dr-customer-offers-ui/lib-interfaces';
import { Store } from '@ngrx/store';
import { NgxIntervalDataGridRowModel, WeekDays } from '@ngx-interval-data-grid';
import moment from 'moment-timezone';
import { filter, Observable, of } from 'rxjs';
import { GroupedData, OffersDataInterface, TabOption } from '../../../../shared/models';
import { UIState } from '../../../../shared/models/utility';
import { DataViewModelService } from '../../../../shared/services/data-vm.service';
import { DateService } from '../../../../shared/services/date.service';
import { InternalService } from '../../../../shared/services/internal.service';
import { MixPanelService } from '../../../../shared/services/mixpanel.service';
import { AppState } from '../../../../store';
import * as Actions from '../../../../store/actions/com.actions';
import * as Selectors from '../../../../store/selectors/com.selectors';
import { FiltersStateService } from '../../../filters/filters-state.service';

@Component({
  selector: 'dr-customer-offers-ui-cleared-table',
  templateUrl: './cleared-tab-table.component.html',
  styleUrls: ['./cleared-tab-table.component.scss'],
  animations: [trigger('detailExpand', [state('collapsed', style({ height: '0px', minHeight: '0' })), state('expanded', style({ height: '*' }))])],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ClearedTabTableComponent implements OnInit {
  protected clearedOffersLoading = toSignal(this.store.select(Selectors.selectClearedOffersLoading), { initialValue: false });
  protected currentRegViewConfigSignal = toSignal(this.store.select(Selectors.selectCurrentRegViewConfig), { initialValue: null });
  groupedDataSignal = signal<GroupedData | null>(null);
  UIState = UIState;
  public dates: number[] = [];
  public timezoneAbbr!: string;

  protected minDate!: moment.Moment;
  protected maxDate!: moment.Moment;

  // Define column headers and fields
  columns: string[] = WeekDays;
  displayedColumns: string[] = ['offer', 'time_period', ...this.columns];
  serverData: NgxIntervalDataGridRowModel[] = [];
  public dataSource = new MatTableDataSource<NgxIntervalDataGridRowModel>(this.serverData);
  public hasClearedOffers = false;
  private selectedRegControl: FormControl = this.dataVMService.selectedRegFormControl;
  protected displaySelectedReg = '';
  protected dateLabel$: Observable<string | null> = of(null);
  userLocale = computed(() => this.internalService.userPreferenceSignal()?.locale ?? 'en-US');

  protected expandedRow!: NgxIntervalDataGridRowModel | null;
  protected showDownloadUploadOptionsSignal = signal(false);

  protected isBulkImportAllowed = computed(() => {
    const allowImport = this.currentRegViewConfigSignal()?.gateRules?.post_gate_edits?.allow_offer_edit;
    return !!allowImport && !!this.currentRegViewConfigSignal()?.allow_cleared_offers_bulk_upload;
  });

  constructor(
    private store: Store<AppState>,
    private mixPanelService: MixPanelService,
    private dataVMService: DataViewModelService,
    private cdr: ChangeDetectorRef,
    private destroyRef: DestroyRef,
    private dateService: DateService,
    private filtersStateService: FiltersStateService,
    private internalService: InternalService
  ) {}

  ngOnInit(): void {
    this.store
      .select(Selectors.selectCurrentRegViewConfig)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((config) => config !== null)
      )
      .subscribe(() => {
        this.loadData();
      });

    this.store
      .select(Selectors.selectCurrentClearedOffers)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((offers) => offers !== null)
      )
      .subscribe((offers: OffersDataInterface | null) => {
        if (offers) this.processClearedData(offers);
        this.filtersStateService.setApplyButtonChanging(this.dataVMService.selectedRegFormControl.value);
      });

    this.mixPanelService.viewTab('Cleared');
  }

  get selectedTabSignal(): WritableSignal<TabOption> {
    return this.dataVMService.selectedTabOptionSignal;
  }

  openExportFunctionalityDialog() {
    this.dataVMService.openExportFunctionalityDialog(undefined, this.minDate, this.maxDate);
  }

  private loadData(): void {
    const selectedDates = this.dataVMService.getAppliedWeekRange();
    const regConfig = this.currentRegViewConfigSignal();

    if (!regConfig || !selectedDates || !selectedDates.start || !selectedDates.end || this.selectedRegControl.value === null) {
      console.warn('Missing required data for loading cleared offers');
      return;
    }

    if (regConfig && !regConfig.graph_view && this.selectedRegControl.value.id) {
      const selectedDatesISO = this.dateService.getISODate(selectedDates, regConfig.timeZone);

      // We need this setTimeOut to make sure we do not dispatch loadClearedOffers with previous filter values especially start and end date.
      setTimeout(() => {
        this.store.dispatch(
          Actions.loadClearedOffers({
            registrationId: regConfig.registrationId,
            startDate: selectedDatesISO.start,
            endDate: selectedDatesISO.end
          })
        );
      }, 10);
    }

    this.displaySelectedReg = regConfig.display_labels || '';
  }

  private processClearedData(offers: OffersDataInterface): void {
    const timeZone = this.currentRegViewConfigSignal()?.timeZone ?? 'UTC';
    this.timezoneAbbr = moment.tz(timeZone).zoneName();
    this.dataSource.data = offers.values ?? [];
    this.hasClearedOffers = this.dataSource.data.some((element) => element.clearedOffers && element.clearedOffers.length > 0);

    if (this.hasClearedOffers && !this.displayedColumns.includes('right_actions')) {
      this.displayedColumns.push('right_actions');
    } else if (!this.hasClearedOffers) {
      this.displayedColumns = this.displayedColumns.filter((col) => col !== 'right_actions');
    }

    const startDate = moment(this.dataSource.data[0].Sunday.start_date_time).tz(timeZone);

    this.dates = [];
    for (let i = 0; i < 7; i++) {
      const newDate = startDate.clone().add(i, 'days');
      this.dates.push(newDate.date());
    }

    const start = moment(this.dataSource.data[0].Sunday.start_date_time).tz(timeZone).locale(this.userLocale()).format('L');
    const end = moment(this.dataSource.data[0].Saturday.start_date_time).tz(timeZone).locale(this.userLocale()).format('L');
    this.dateLabel$ = of(`${start} - ${end}`);

    this.cdr.detectChanges();
  }

  // ---------------------------------------------------------------------------
  // Download / Upload menu
  // ---------------------------------------------------------------------------

  toggleDownloadUploadOptions(event: MouseEvent): void {
    event.stopPropagation();
    this.showDownloadUploadOptionsSignal.update((show) => !show);
  }

  clickOutsideDownloadUploadOptions(): void {
    this.showDownloadUploadOptionsSignal.update((show) => (show ? false : show));
  }

  openBulkUploadFunctionalityDialog(): void {
    const regConfig = this.currentRegViewConfigSignal();
    this.dataVMService.openBulkUploadFunctionalityDialog({
      minDate: this.minDate,
      maxDate: this.maxDate,
      registrationId: regConfig?.registrationId ?? '',
      intervalFrequency: regConfig?.intervalFrequency ?? 60,
      timeZone: regConfig?.timeZone ?? 'UTC',
      importType: BulkImportType.Offers,
      registrationName: regConfig?.display_labels ?? '',
      offer_dimensions: regConfig?.graph_config?.offer_dimensions ?? null
    });
  }
}
